import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import ArtistLayout from '../components/layout/ArtistLayout';
import { 
  MessageSquare, 
  Star,
  AlertTriangle,
  ThumbsUp,
  Calendar,
  TrendingUp
} from 'lucide-react';
import { cn } from '../lib/utils';
import { motion } from 'framer-motion';

const ArtistFeedback = () => {
  const { isDark } = useTheme();
  const [feedback, setFeedback] = useState({ positive_feedback: [], escalations: [] });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('positive');

  useEffect(() => {
    fetchFeedback();
  }, []);

  const fetchFeedback = async () => {
    try {
      setLoading(true);
      const response = await api.get('/artist/feedback');
      setFeedback(response.data);
    } catch (error) {
      console.error('Error fetching feedback:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMonthName = (month) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[month - 1] || '';
  };

  return (
    <ArtistLayout>
      <div className="max-w-4xl mx-auto space-y-6 animate-fadeIn" data-testid="artist-feedback">
        {/* Header */}
        <div>
          <h1 className={cn("text-2xl font-bold font-outfit flex items-center gap-2", isDark ? "text-white" : "text-slate-800")}>
            <MessageSquare className="w-7 h-7 text-[#331062]" />
            Feedback & Recognition
          </h1>
          <p className={cn("text-sm mt-1", isDark ? "text-slate-400" : "text-slate-500")}>
            View your positive feedback and escalation history
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4">
          <Card className={cn(
            "p-6 cursor-pointer transition-all",
            activeTab === 'positive' && "ring-2 ring-green-500",
            isDark ? "bg-slate-800 border-slate-700" : ""
          )} onClick={() => setActiveTab('positive')}>
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-green-100 dark:bg-green-900/50">
                <Star className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className={cn("text-3xl font-bold text-green-600")}>
                  {feedback.positive_feedback?.length || 0}
                </p>
                <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>
                  Positive Feedback
                </p>
              </div>
            </div>
          </Card>
          <Card className={cn(
            "p-6 cursor-pointer transition-all",
            activeTab === 'escalations' && "ring-2 ring-red-500",
            isDark ? "bg-slate-800 border-slate-700" : ""
          )} onClick={() => setActiveTab('escalations')}>
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-red-100 dark:bg-red-900/50">
                <AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className={cn("text-3xl font-bold text-red-600")}>
                  {feedback.escalations?.length || 0}
                </p>
                <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>
                  Escalations
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Tab Content */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-[#331062] border-t-transparent rounded-full" />
          </div>
        ) : (
          <Card className={cn("p-6", isDark ? "bg-slate-800 border-slate-700" : "")}>
            <h3 className={cn("text-lg font-semibold mb-4 flex items-center gap-2", isDark ? "text-white" : "text-slate-800")}>
              {activeTab === 'positive' ? (
                <>
                  <ThumbsUp className="w-5 h-5 text-green-500" />
                  Positive Feedback History
                </>
              ) : (
                <>
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  Escalation History
                </>
              )}
            </h3>

            {(activeTab === 'positive' ? feedback.positive_feedback : feedback.escalations)?.length === 0 ? (
              <div className="text-center py-8">
                {activeTab === 'positive' ? (
                  <>
                    <Star className={cn("w-16 h-16 mx-auto mb-3", isDark ? "text-slate-600" : "text-slate-300")} />
                    <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>
                      No positive feedback recorded yet. Keep up the good work!
                    </p>
                  </>
                ) : (
                  <>
                    <TrendingUp className={cn("w-16 h-16 mx-auto mb-3 text-green-500")} />
                    <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>
                      Great job! No escalations on your record.
                    </p>
                  </>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {(activeTab === 'positive' ? feedback.positive_feedback : feedback.escalations).map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={cn(
                      "p-4 rounded-lg border-l-4",
                      activeTab === 'positive'
                        ? "border-green-500 bg-green-50 dark:bg-green-900/20"
                        : "border-red-500 bg-red-50 dark:bg-red-900/20"
                    )}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="w-4 h-4 text-slate-500" />
                      <span className={cn("text-sm font-medium", isDark ? "text-white" : "text-slate-700")}>
                        Week {item.week}, {getMonthName(item.month)} {item.year}
                      </span>
                    </div>
                    {item.comments && (
                      <p className={cn("text-sm", isDark ? "text-slate-300" : "text-slate-600")}>
                        {item.comments}
                      </p>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
          </Card>
        )}
      </div>
    </ArtistLayout>
  );
};

export default ArtistFeedback;
